import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
  FaFacebookF,
  FaGlobe,
  FaHeart,
  FaInfoCircle,
  FaInstagram,
  FaMapMarkerAlt,
  FaRegCalendarAlt,
  FaRegHeart,
  FaShareAlt,
  FaStore,
  FaTiktok,
  FaYoutube,
} from "react-icons/fa";
import { useFavorites } from "./FavoritesContext";
import CommunityEvents from "./CommunityEvents";
import EventDetailsSheet from "./EventDetailsSheet";
import PostEventSheet from "./PostEventSheet";
import { useCommunityEvents, useEventSaves } from "./eventsApi";
import "./CommunityShowcase.css";
import "./CommunityShowcasePhotos.css";

/* =========================================================
   CommunityShowcase   the "Inside a community" design,
   v3: adds the opt-in Events layer.

   BUSINESS PHOTO UPDATE
   ---------------------
   If a business record contains:
     cover_photo / coverPhoto
     photos: [url1, url2, ...]
   the first/cover photo is used in Featured and All Businesses.
   Businesses with no photo keep the existing emoji fallback.
   ========================================================= */

const EVENTS_LABELS = {
  american: "Local events",
};

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

const nameOf = (b) =>
  firstText(b?.name, b?.business_name, b?.businessName) || "Unnamed business";

const categoryOf = (b) =>
  firstText(
    b?.category,
    b?.api_category,
    b?.apiCategory,
    b?.business_type,
    b?.businessType,
    b?.type
  );

const specialtyOf = (b) =>
  Array.isArray(b?.specialties) ? firstText(b.specialties[0]) : "";

const phoneOf = (b) =>
  firstText(b?.phoneNumber, b?.phone_number, b?.phone);

const addressOf = (b) =>
  firstText(
    b?.address,
    b?.full_address,
    b?.fullAddress,
    [
      firstText(b?.street_address, b?.streetAddress),
      firstText(b?.city),
      firstText(b?.state),
    ]
      .filter(Boolean)
      .join(", ")
  );

const mapUrlOf = (b) => firstText(b?.map_url, b?.mapUrl);

const profilePathOf = (b) => firstText(b?.profile_path, b?.profilePath);

const idOf = (b, index) => b?.id || `${nameOf(b)}-${index}`;

const isPremium = (b) =>
  /premium|paid/i.test(firstText(b?.listing_plan, b?.listingPlan, b?.plan)) ||
  b?.legacy_premium_label === true ||
  b?.isPremium === true;

const submittedAt = (b) => {
  const raw = firstText(
    b?.submitted_at,
    b?.submittedAt,
    b?.created_at,
    b?.createdAt,
    b?.submitted
  );
  const time = raw ? Date.parse(raw) : NaN;
  return Number.isFinite(time) ? time : 0;
};

const CATEGORY_EMOJI = {
  restaurants: "🍴",
  restaurant: "🍴",
  "food truck": "🚚",
  foodtruck: "🚚",
  "food trucks": "🚚",
  groceries: "🛒",
  grocery: "🛒",
  "grocery stores": "🛒",
  "beauty services": "💄",
  beauty: "💄",
  catering: "🍲",
  boutiques: "👗",
  clothing: "👗",
  photography: "📷",
  jewelry: "💎",
  "child care": "👶",
  "cleaning services": "🧽",
  "it services": "💻",
  plumbing: "🔧",
  "electrical services": "⚡",
  gardening: "🌱",
  realtors: "🏘️",
  realtor: "🏘️",
  "home help": "🏡",
  "local restaurants": "🍴",
  toys: "🧸",
};

const emojiFor = (b) =>
  CATEGORY_EMOJI[categoryOf(b).toLowerCase()] || "🏪";

const metaLine = (b) =>
  [categoryOf(b), specialtyOf(b) || firstText(b?.city), phoneOf(b)]
    .filter(Boolean)
    .join(" · ");

/* ---------- business photo helpers ---------- */

const parseBusinessDetails = (business) => {
  if (
    business?.details &&
    typeof business.details === "object" &&
    !Array.isArray(business.details)
  ) {
    return business.details;
  }

  if (
    business?.details_json &&
    typeof business.details_json === "object" &&
    !Array.isArray(business.details_json)
  ) {
    return business.details_json;
  }

  if (typeof business?.details_json === "string") {
    try {
      const parsed = JSON.parse(business.details_json);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }

  return {};
};

const businessPhotosOf = (business) => {
  const details = parseBusinessDetails(business);

  const candidates = [
    business?.cover_photo,
    business?.coverPhoto,
    details?.cover_photo,
    details?.coverPhoto,
    ...(Array.isArray(business?.photos) ? business.photos : []),
    ...(Array.isArray(details?.photos) ? details.photos : []),
  ];

  const output = [];
  const seen = new Set();

  candidates.forEach((value) => {
    const clean = firstText(value);
    if (!clean) return;

    const key = clean.toLowerCase();
    if (seen.has(key)) return;

    seen.add(key);
    output.push(clean);
  });

  return output.slice(0, 4);
};

const coverPhotoOf = (business) => businessPhotosOf(business)[0] || "";

function BusinessVisual({
  business,
  index,
  variant = "row",
}) {
  const photo = coverPhotoOf(business);
  const photoCount = businessPhotosOf(business).length;
  const classPrefix = variant === "featured" ? "ci-card" : "ci-row";

  return (
    <span
      className={`${classPrefix}__ph ci-ph-${index % 4} ${
        photo ? `${classPrefix}__ph--photo` : ""
      }`}
      aria-hidden="true"
    >
      {photo ? (
        <img
          className={`${classPrefix}__photo`}
          src={photo}
          alt=""
          loading="lazy"
        />
      ) : (
        emojiFor(business)
      )}

      {variant === "featured" && isPremium(business) && (
        <span className="ci-card__star">★</span>
      )}

      {photo && photoCount > 1 && (
        <span className="ci-business-photo-count">
          +{photoCount - 1}
        </span>
      )}
    </span>
  );
}

/* Hero backdrop photo */
const tryHeroArt = (name) => {
  if (!name) return null;
  try {
    /* eslint-disable-next-line global-require, import/no-dynamic-require */
    return require(`./heroArt/${name}.jpg`);
  } catch {
    return null;
  }
};
const DEFAULT_BACKDROP = tryHeroArt("sa-riverwalk");

export default function CommunityShowcase({
  title = "Community",
  icon = "🌍",
  businesses = [],
  loading = false,
  error = "",
  isFavorite = null,
  onToggleFavorite = null,
  onSelectBusiness = null,
  deal = null,
  backFallback = "/connect",
  communitySlug = "",
  links = null,
  onPostEvent = null,
}) {
  const navigate = useNavigate();
  const [activeChip, setActiveChip] = useState("All");

  const backdrop = useMemo(
    () =>
      tryHeroArt(
        communitySlug
          ? `community-${String(communitySlug).trim().toLowerCase()}`
          : ""
      ) || DEFAULT_BACKDROP,
    [communitySlug]
  );

  const { isSaved, toggleSave, showToast, user, requestSignInPrompt } =
    useFavorites();

  const eventsEnabled = Boolean(communitySlug);
  const eventsLabel =
    EVENTS_LABELS[String(communitySlug).trim().toLowerCase()] || "Events";

  const {
    events,
    importantDates,
    links: fetchedLinks,
    loading: eventsLoading,
    error: eventsError,
  } = useCommunityEvents(eventsEnabled ? communitySlug : "");

  const socialLinks = links || fetchedLinks;

  const { isEventSaved, toggleEventSave } = useEventSaves(user);

  const [postOpen, setPostOpen] = useState(false);

  const featRef = useRef(null);
  const [featScroll, setFeatScroll] = useState({
    left: false,
    right: false,
  });

  const updateFeatArrows = () => {
    const el = featRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth - 2;
    setFeatScroll({
      left: el.scrollLeft > 2,
      right: el.scrollLeft < max,
    });
  };

  useEffect(() => {
    const raf = window.requestAnimationFrame(updateFeatArrows);
    window.addEventListener("resize", updateFeatArrows);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", updateFeatArrows);
    };
  });

  const pageFeat = (dir) => {
    const el = featRef.current;
    if (!el) return;

    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    el.scrollBy({
      left: dir * el.clientWidth * 0.85,
      behavior: reduce ? "auto" : "smooth",
    });
  };

  const [pendingPost, setPendingPost] = useState(false);

  const openPostFlow = () => {
    if (user) {
      setPostOpen(true);
      return;
    }

    setPendingPost(true);

    requestSignInPrompt({
      kind: "event",
      name: title,
      onCancel: () => setPendingPost(false),
    });
  };

  useEffect(() => {
    if (pendingPost && user) {
      setPendingPost(false);
      setPostOpen(true);
    }
  }, [pendingPost, user]);

  const handleToggleEventSave = async (ev) => {
    if (!user) {
      requestSignInPrompt({
        kind: "event-save",
        name: ev.title,
      });
      return;
    }

    try {
      const saved = await toggleEventSave(ev.id);
      showToast(saved ? `Saved ${ev.title} ♥` : "Removed from saved");
    } catch {
      showToast("Couldn't update saved events");
    }
  };

  const [searchParams, setSearchParams] = useSearchParams();

  const tab =
    eventsEnabled && searchParams.get("tab") === "events"
      ? "events"
      : "businesses";

  const setTab = (next) => {
    if (!eventsEnabled) return;

    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);

        if (next === "events") {
          p.set("tab", "events");
        } else {
          p.delete("tab");
          p.delete("event");
        }

        return p;
      },
      { replace: true }
    );
  };

  const eventParam = eventsEnabled ? searchParams.get("event") : null;

  const activeEvent = useMemo(
    () =>
      eventParam
        ? events.find((ev) => String(ev.id) === String(eventParam)) || null
        : null,
    [events, eventParam]
  );

  const openEvent = (ev) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("tab", "events");
        p.set("event", String(ev.id));
        return p;
      },
      { replace: true }
    );
  };

  const closeEvent = () => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete("event");
        return p;
      },
      { replace: true }
    );
  };

  const list = Array.isArray(businesses) ? businesses : [];

  const newThisWeek = useMemo(() => {
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return list.filter((b) => submittedAt(b) > weekAgo).length;
  }, [list]);

  const chips = useMemo(() => {
    const unique = [];

    list.forEach((b) => {
      const cat = categoryOf(b);
      if (cat && !unique.includes(cat)) unique.push(cat);
    });

    return unique.slice(0, 8);
  }, [list]);

  const filtered = useMemo(
    () =>
      activeChip === "All"
        ? list
        : list.filter((b) => categoryOf(b) === activeChip),
    [list, activeChip]
  );

  const featured = useMemo(() => {
    const sorted = [...list].sort((a, b) => {
      const premiumDiff =
        Number(isPremium(b)) - Number(isPremium(a));

      return premiumDiff !== 0
        ? premiumDiff
        : submittedAt(b) - submittedAt(a);
    });

    return sorted.slice(0, 6);
  }, [list]);

  const goBack = () => {
    const idx =
      typeof window !== "undefined" && window.history.state
        ? window.history.state.idx
        : 0;

    if (idx > 0) navigate(-1);
    else navigate(backFallback);
  };

  const toggleBizFav = (business) => {
    toggleSave(business?.id, nameOf(business));
  };

  const openMap = (business) => {
    const direct = mapUrlOf(business);

    if (direct) {
      window.open(direct, "_blank", "noopener");
      return;
    }

    const query =
      addressOf(business) || `${nameOf(business)} San Antonio`;

    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        query
      )}`,
      "_blank",
      "noopener"
    );
  };

  const shareBusiness = async (business) => {
    const profilePath = profilePathOf(business);

    const url = profilePath
      ? `${window.location.origin}${profilePath}`
      : window.location.href;

    const text = `${nameOf(business)} on GoOurHub`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: text,
          text,
          url,
        });
        return;
      }
    } catch {
      return;
    }

    try {
      await navigator.clipboard.writeText(`${text} — ${url}`);
      window.alert("Link copied.");
    } catch {
      window.alert(url);
    }
  };

  const rowTap = (business) => {
    if (typeof onSelectBusiness === "function") {
      onSelectBusiness(business);
    }
  };

  return (
    <section className="ci">
      <header
        className={`ci-hero${backdrop ? " ci-hero--photo" : ""}`}
        style={
          backdrop
            ? {
                "--sa-photo": `url(${backdrop})`,
              }
            : undefined
        }
      >
        <div className="ci-hero__nav">
          <button
            type="button"
            className="ci-hero__btn"
            onClick={goBack}
            aria-label="Go back"
          >
            <FaArrowLeft />
          </button>

          {typeof onToggleFavorite === "function" && (
            <button
              type="button"
              className={`ci-hero__btn ci-hero__btn--love ${
                isFavorite ? "is-on" : ""
              }`}
              onClick={onToggleFavorite}
              aria-pressed={!!isFavorite}
              aria-label={
                isFavorite
                  ? "Remove from favorites"
                  : "Add to favorites"
              }
            >
              {isFavorite ? <FaHeart /> : <FaRegHeart />}
            </button>
          )}
        </div>

        <div className="ci-hero__id">
          <span
            className="ci-hero__flag"
            aria-hidden="true"
          >
            {icon}
          </span>

          <span>
            <h1>{title}</h1>

            <p className="ci-hero__stats">
              <b>
                {list.length} business
                {list.length === 1 ? "" : "es"}
              </b>

              {newThisWeek > 0 && (
                <>
                  {" "}
                  ·{" "}
                  <b className="ci-gold">
                    {newThisWeek} new
                  </b>{" "}
                  this week
                </>
              )}
            </p>

            {socialLinks &&
              (socialLinks.website ||
                socialLinks.instagram ||
                socialLinks.facebook ||
                socialLinks.youtube ||
                socialLinks.tiktok) && (
                <span className="ci-hero__soc">
                  {socialLinks.website && (
                    <a
                      href={socialLinks.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${title} website`}
                    >
                      <FaGlobe />
                    </a>
                  )}

                  {socialLinks.instagram && (
                    <a
                      href={socialLinks.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${title} on Instagram`}
                    >
                      <FaInstagram />
                    </a>
                  )}

                  {socialLinks.facebook && (
                    <a
                      href={socialLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${title} on Facebook`}
                    >
                      <FaFacebookF />
                    </a>
                  )}

                  {socialLinks.youtube && (
                    <a
                      href={socialLinks.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${title} on YouTube`}
                    >
                      <FaYoutube />
                    </a>
                  )}

                  {socialLinks.tiktok && (
                    <a
                      href={socialLinks.tiktok}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${title} on TikTok`}
                    >
                      <FaTiktok />
                    </a>
                  )}
                </span>
              )}
          </span>
        </div>

        {eventsEnabled && (
          <nav
            className="ci-tabs"
            aria-label="Community sections"
          >
            <button
              type="button"
              className={`ci-tab ${
                tab === "businesses" ? "is-on" : ""
              }`}
              onClick={() => setTab("businesses")}
            >
              <FaStore aria-hidden="true" />
              Businesses
            </button>

            <button
              type="button"
              className={`ci-tab ${
                tab === "events" ? "is-on" : ""
              }`}
              onClick={() => setTab("events")}
            >
              <FaRegCalendarAlt aria-hidden="true" />
              {eventsLabel}

              {events.length > 0 && (
                <span className="ci-tab__n">
                  {events.length}
                </span>
              )}
            </button>
          </nav>
        )}
      </header>

      {tab === "businesses" && (
        <>
          {chips.length > 1 && (
            <div
              className="ci-chips"
              role="tablist"
              aria-label="Filter by category"
            >
              {["All", ...chips].map((chip) => (
                <button
                  key={chip}
                  type="button"
                  role="tab"
                  aria-selected={activeChip === chip}
                  className={`ci-chip ${
                    activeChip === chip ? "is-on" : ""
                  }`}
                  onClick={() => setActiveChip(chip)}
                >
                  {chip}
                </button>
              ))}
            </div>
          )}

          {deal && (
            <div className="ci-deal">
              <span
                className="ci-deal__ic"
                aria-hidden="true"
              >
                {deal.icon || "🎉"}
              </span>

              <span>
                <span className="ci-deal__k">
                  Community deal
                </span>

                <span className="ci-deal__t">
                  {deal.text}
                </span>
              </span>
            </div>
          )}

          {loading && (
            <p
              className="ci-note"
              role="status"
            >
              Loading businesses…
            </p>
          )}

          {!loading && error && (
            <p
              className="ci-note ci-note--err"
              role="alert"
            >
              {error}
            </p>
          )}

          {!loading &&
            !error &&
            list.length === 0 && (
              <p className="ci-note">
                No businesses here yet — be the first to list one.
              </p>
            )}

          {!loading && featured.length > 1 && (
            <>
              <div className="ci-railhead">
                <h2>Featured</h2>

                <span className="ci-railend">
                  <span>
                    {list.length} total
                  </span>

                  <span className="ci-railnav">
                    <button
                      type="button"
                      aria-label="Scroll featured left"
                      disabled={!featScroll.left}
                      onClick={() => pageFeat(-1)}
                    >
                      <FaChevronLeft />
                    </button>

                    <button
                      type="button"
                      aria-label="Scroll featured right"
                      disabled={!featScroll.right}
                      onClick={() => pageFeat(1)}
                    >
                      <FaChevronRight />
                    </button>
                  </span>
                </span>
              </div>

              <div
                className="ci-cards"
                ref={featRef}
                onScroll={updateFeatArrows}
              >
                {featured.map((business, index) => (
                  <button
                    key={`f-${idOf(
                      business,
                      index
                    )}`}
                    type="button"
                    className="ci-card"
                    onClick={() =>
                      rowTap(business)
                    }
                  >
                    <BusinessVisual
                      business={business}
                      index={index}
                      variant="featured"
                    />

                    <span className="ci-card__bd">
                      <span className="ci-card__nm">
                        {nameOf(business)}
                      </span>

                      <span className="ci-card__tg">
                        {[
                          categoryOf(
                            business
                          ),
                          specialtyOf(
                            business
                          ),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}

          {!loading &&
            filtered.length > 0 && (
              <>
                <div className="ci-railhead">
                  <h2>
                    {activeChip === "All"
                      ? "All businesses"
                      : activeChip}
                  </h2>
                </div>

                <div className="ci-rows">
                  {filtered.map(
                    (
                      business,
                      index
                    ) => {
                      const key =
                        idOf(
                          business,
                          index
                        );

                      const saved =
                        isSaved(
                          business?.id
                        );

                      return (
                        <div
                          key={key}
                          className="ci-row"
                          data-business-id={
                            business?.id
                          }
                        >
                          <button
                            type="button"
                            className="ci-row__main"
                            onClick={() =>
                              rowTap(
                                business
                              )
                            }
                          >
                            <BusinessVisual
                              business={
                                business
                              }
                              index={
                                index
                              }
                            />

                            <span className="ci-row__bd">
                              <span className="ci-row__nm">
                                {nameOf(
                                  business
                                )}
                              </span>

                              <span className="ci-row__tg">
                                {metaLine(
                                  business
                                )}
                              </span>
                            </span>
                          </button>

                          <span className="ci-row__acts">
                            <button
                              type="button"
                              className={`ci-act ${
                                saved
                                  ? "is-on"
                                  : ""
                              }`}
                              onClick={() =>
                                toggleBizFav(
                                  business
                                )
                              }
                              aria-pressed={
                                saved
                              }
                              aria-label={
                                saved
                                  ? "Remove from saved"
                                  : "Save"
                              }
                            >
                              {saved ? (
                                <FaHeart />
                              ) : (
                                <FaRegHeart />
                              )}
                            </button>

                            <button
                              type="button"
                              className="ci-act"
                              onClick={() =>
                                openMap(
                                  business
                                )
                              }
                              aria-label="Open in Maps"
                            >
                              <FaMapMarkerAlt />
                            </button>

                            <button
                              type="button"
                              className="ci-act"
                              onClick={() =>
                                shareBusiness(
                                  business
                                )
                              }
                              aria-label="Share"
                            >
                              <FaShareAlt />
                            </button>

                            {typeof onSelectBusiness ===
                              "function" && (
                              <button
                                type="button"
                                className="ci-act"
                                onClick={() =>
                                  rowTap(
                                    business
                                  )
                                }
                                aria-label="Details"
                              >
                                <FaInfoCircle />
                              </button>
                            )}

                            <span
                              className="ci-row__go"
                              aria-hidden="true"
                            >
                              <FaArrowRight />
                            </span>
                          </span>
                        </div>
                      );
                    }
                  )}
                </div>
              </>
            )}
        </>
      )}

      {eventsEnabled &&
        tab === "events" && (
          <CommunityEvents
            community={title}
            eventsLabel={eventsLabel}
            events={events}
            importantDates={importantDates}
            loading={eventsLoading}
            error={eventsError}
            onOpenEvent={openEvent}
            onPostEvent={
              onPostEvent ||
              openPostFlow
            }
            isEventSaved={
              isEventSaved
            }
            onToggleSave={
              handleToggleEventSave
            }
          />
        )}

      {eventsEnabled &&
        activeEvent && (
          <EventDetailsSheet
            event={activeEvent}
            onClose={closeEvent}
            isEventSaved={
              isEventSaved
            }
            onToggleSave={
              handleToggleEventSave
            }
          />
        )}

      {eventsEnabled &&
        postOpen && (
          <PostEventSheet
            community={title}
            communitySlug={
              communitySlug
            }
            initialEmail={
              user?.email || ""
            }
            onClose={() =>
              setPostOpen(
                false
              )
            }
          />
        )}
    </section>
  );
}
